import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Plus, Trash2, Save, Download, FileText, Info, ShoppingCart, Mail } from "lucide-react";
import html2canvas from "html2canvas";
import QuoteTargetSelector from "@/components/QuoteTargetSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Product definitions with linkages
const PRODUCTS = [
  {
    id: 'ts1000',
    sku: 'TS-TS1000-Pro',
    name: 'TS1000 Pro',
    description: 'Tripod Turnstile - Compact Design',
    category: 'turnstile',
    width: 810,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/wo5ptzys_TS1000Pro.jpg',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/entkewdy_TS1000%20pro-Leaflet-202112%20%281%29.pdf',
    linkedBase: { sku: 'TS1000-Base', name: 'TS1000 Pro Base' },
    requiredWings: null
  },
  {
    id: 'ts2000',
    sku: 'TS-TS2000-Pro',
    name: 'TS2000 Pro',
    description: 'Tripod Turnstile - Bridge Style',
    category: 'turnstile',
    width: 1110,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/v1rhgjoc_TS2000Pro.png',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/dgm1dcfv_TS2000%20pro-Leaflet-202112%20%281%29.pdf',
    linkedBase: { sku: 'TS2000-Base', name: 'TS2000 Pro Base' },
    requiredWings: null
  },
  {
    id: 'sbt3000s',
    sku: 'TS-SBT3000S-SINGLE-OB-ZK',
    name: 'SBT3000S (Single)',
    description: 'Single Swing Barrier Gate',
    category: 'swing_barrier',
    width: 1040,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/rx0wmog6_SBT3000S.jpg',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/meitc6sg_sbt3000.pdf',
    linkedBase: { sku: 'SBT3000S-Base', name: 'SBT3000S Base' },
    requiredWings: { sku: 'SBT3000-800ACR', name: 'SBT3000 Single Acrylic Infil Wings', qty: 1 }
  },
  {
    id: 'sbt3000d',
    sku: 'TS-SBT3000S-DOUBLE-OB-ZK',
    name: 'SBT3000D (Double)',
    description: 'Double Swing Barrier Gate',
    category: 'swing_barrier',
    width: 1610,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/ta7oqknd_SBT3000D.jpg',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/meitc6sg_sbt3000.pdf',
    linkedBase: { sku: 'SBT3000D-Base', name: 'SBT3000D Base' },
    requiredWings: { sku: 'SBT3000-600ACR', name: 'SBT3000 Double Acrylic Infil Wings', qty: 2 }
  }
];

const HalfHeightTurnstiles = ({ onBack }) => {
  const [user, setUser] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [priceModifier, setPriceModifier] = useState(""); // Installer discount percentage
  const [requireBases, setRequireBases] = useState("yes");
  const [quoteTarget, setQuoteTarget] = useState(""); // "distributor" or "installer"
  
  // Quantities for each product
  const [quantities, setQuantities] = useState({
    ts1000: 0,
    ts2000: 0,
    sbt3000s: 0,
    sbt3000d: 0
  });
  
  const [pricing, setPricing] = useState({});
  const [sundryItems, setSundryItems] = useState([]);
  const [showSundryDialog, setShowSundryDialog] = useState(false);
  const [sundryDescription, setSundryDescription] = useState("");
  const [sundryPrice, setSundryPrice] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [quoteGenerated, setQuoteGenerated] = useState(false);
  const [generatedBOM, setGeneratedBOM] = useState([]);
  const [generatedTotal, setGeneratedTotal] = useState(0);
  const [installerOnlyCapture, setInstallerOnlyCapture] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchPricing();
  }, []);

  // BOM pricing visibility based on quoteTarget for admin users
  const userRole = user?.role || '';
  const isAdmin = userRole === 'admin' || userRole === 'zkteco_admin' || userRole === 'zkteco';
  const isDistributor = userRole === 'distributor' || userRole === 'distributor_admin';
  const showBomDistributorPrice = !installerOnlyCapture && ((isAdmin && quoteTarget === 'distributor') || isDistributor);
  const showBomInstallerPrice = isAdmin ? (quoteTarget === 'distributor' || quoteTarget === 'installer') : (isDistributor || userRole === 'installer');
  const showBomEndUserPrice = isAdmin && quoteTarget === 'end_user';

  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/half-height-turnstiles/pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(response.data);
    } catch (err) {
      console.error('Error fetching pricing:', err);
    }
  };

  const getPriceForSku = (sku) => {
    if (!pricing[sku]) return { distributor: 0, installer: 0, enduser: 0 };
    return pricing[sku];
  };

  const getUserPriceKey = () => {
    if (!user) return 'enduser';
    const role = user.role;
    if (role === 'admin' || role === 'zkteco_admin' || role === 'zkteco' || 
        role === 'distributor' || role === 'distributor_admin') {
      return 'distributor';
    }
    if (role === 'installer' || role === 'subdistributor') {
      return 'installer';
    }
    return 'enduser';
  };

  const handleQuantityChange = (productId, value) => {
    const qty = parseInt(value) || 0;
    setQuantities(prev => ({ ...prev, [productId]: Math.max(0, qty) }));
    // Reset quote when quantities change - user must regenerate
    setQuoteGenerated(false);
    setGeneratedBOM([]);
    setGeneratedTotal(0);
  };

  const handleRemoveSundryItem = (id) => {
    setSundryItems(prev => prev.filter(item => item.id !== id));
    // Reset quote when sundry items change
    setQuoteGenerated(false);
    setGeneratedBOM([]);
    setGeneratedTotal(0);
  };

  const handleAddSundryItem = () => {
    if (!sundryDescription.trim() || !sundryPrice) return;
    setSundryItems(prev => [...prev, {
      id: Date.now(),
      description: sundryDescription.trim(),
      price: parseFloat(sundryPrice)
    }]);
    setSundryDescription("");
    setSundryPrice("");
    setShowSundryDialog(false);
    // Reset quote when sundry items change
    setQuoteGenerated(false);
    setGeneratedBOM([]);
    setGeneratedTotal(0);
  };

  const handleClearAll = () => {
    setQuantities({ ts1000: 0, ts2000: 0, sbt3000s: 0, sbt3000d: 0 });
    setSundryItems([]);
    setQuoteGenerated(false);
    setGeneratedBOM([]);
    setGeneratedTotal(0);
  };

  // Check if any products are selected
  const hasProducts = Object.values(quantities).some(qty => qty > 0);

  // Calculate BOM items
  const getBOMItems = () => {
    const items = [];
    const priceKey = getUserPriceKey();
    const discountMultiplier = priceModifier ? (1 - parseFloat(priceModifier) / 100) : 1;

    PRODUCTS.forEach(product => {
      const qty = quantities[product.id];
      if (qty > 0) {
        // Main product
        const productPrice = getPriceForSku(product.sku);
        const distributorUnitPrice = productPrice['distributor'] || 0;
        const installerUnitPrice = (productPrice['installer'] || 0) * discountMultiplier;
        const endUserUnitPrice = productPrice['enduser'] || 0;
        items.push({
          sku: product.sku,
          description: product.name,
          qty: qty,
          unitPrice: productPrice[priceKey] || 0,
          total: (productPrice[priceKey] || 0) * qty,
          distributorPrice: distributorUnitPrice,
          distributorTotal: distributorUnitPrice * qty,
          installerPrice: installerUnitPrice,
          installerTotal: installerUnitPrice * qty,
          endUserPrice: endUserUnitPrice,
          endUserTotal: endUserUnitPrice * qty,
          type: 'product'
        });

        // Required wings (mandatory for SBT3000)
        if (product.requiredWings) {
          const wingsPrice = getPriceForSku(product.requiredWings.sku);
          const wingsQty = product.requiredWings.qty * qty;
          const wingsDistPrice = wingsPrice['distributor'] || 0;
          const wingsInstPrice = (wingsPrice['installer'] || 0) * discountMultiplier;
          const wingsEndUserPrice = wingsPrice['enduser'] || 0;
          items.push({
            sku: product.requiredWings.sku,
            description: product.requiredWings.name,
            qty: wingsQty,
            unitPrice: wingsPrice[priceKey] || 0,
            total: (wingsPrice[priceKey] || 0) * wingsQty,
            distributorPrice: wingsDistPrice,
            distributorTotal: wingsDistPrice * wingsQty,
            installerPrice: wingsInstPrice,
            installerTotal: wingsInstPrice * wingsQty,
            endUserPrice: wingsEndUserPrice,
            endUserTotal: wingsEndUserPrice * wingsQty,
            type: 'wings'
          });
        }

        // Optional base
        if (requireBases === 'yes' && product.linkedBase) {
          const basePrice = getPriceForSku(product.linkedBase.sku);
          const baseDistPrice = basePrice['distributor'] || 0;
          const baseInstPrice = basePrice['installer'] || 0;
          const baseEndUserPrice = basePrice['enduser'] || 0;
          items.push({
            sku: product.linkedBase.sku,
            description: product.linkedBase.name,
            qty: qty,
            unitPrice: basePrice[priceKey] || 0,
            total: (basePrice[priceKey] || 0) * qty,
            distributorPrice: baseDistPrice,
            distributorTotal: baseDistPrice * qty,
            installerPrice: baseInstPrice,
            installerTotal: baseInstPrice * qty,
            endUserPrice: baseEndUserPrice,
            endUserTotal: baseEndUserPrice * qty,
            type: 'base'
          });
        }
      }
    });

    // Calculate delivery cost from pricing data
    const turnstileDelivery = getPriceForSku('Delivery-Turnstile');
    const swingBarrierDelivery = getPriceForSku('Delivery-SwingBarrier');
    const baseDelivery = getPriceForSku('Delivery-Base');

    let totalDeliveryDist = 0;
    let totalDeliveryInst = 0;
    let totalDeliveryEndUser = 0;
    let totalDeliveryParts = 0;

    PRODUCTS.forEach(product => {
      const qty = quantities[product.id];
      if (qty > 0) {
        const isSwingBarrier = product.category === 'swing_barrier';
        const delPrice = isSwingBarrier ? swingBarrierDelivery : turnstileDelivery;
        totalDeliveryDist += (delPrice.distributor || 0) * qty;
        totalDeliveryInst += (delPrice.installer || 0) * qty;
        totalDeliveryEndUser += (delPrice.enduser || 0) * qty;
        totalDeliveryParts += qty;

        // Delivery for bases
        if (requireBases === 'yes' && product.linkedBase) {
          totalDeliveryDist += (baseDelivery.distributor || 0) * qty;
          totalDeliveryInst += (baseDelivery.installer || 0) * qty;
          totalDeliveryEndUser += (baseDelivery.enduser || 0) * qty;
          totalDeliveryParts += qty;
        }
      }
    });

    if (totalDeliveryParts > 0) {
      const deliveryUnitPrice = priceKey === 'distributor' ? totalDeliveryDist : priceKey === 'installer' ? totalDeliveryInst : totalDeliveryEndUser;
      items.push({
        sku: 'DELIVERY',
        description: 'Delivery to Mainland GB',
        qty: 1,
        unitPrice: deliveryUnitPrice,
        total: deliveryUnitPrice,
        distributorPrice: totalDeliveryDist,
        distributorTotal: totalDeliveryDist,
        installerPrice: totalDeliveryInst,
        installerTotal: totalDeliveryInst,
        endUserPrice: totalDeliveryEndUser,
        endUserTotal: totalDeliveryEndUser,
        type: 'delivery'
      });
    }

    // Add sundry items
    sundryItems.forEach(item => {
      items.push({
        sku: 'SUNDRY',
        description: item.description,
        qty: 1,
        unitPrice: item.price,
        total: item.price,
        distributorPrice: item.price,
        distributorTotal: item.price,
        installerPrice: item.price,
        installerTotal: item.price,
        endUserPrice: item.price,
        endUserTotal: item.price,
        type: 'sundry',
        id: item.id
      });
    });

    return items;
  };

  // Calculate BOM items for preview only (not saved until user commits)
  const calculateBOM = () => {
    const items = getBOMItems();
    const total = items.reduce((sum, item) => sum + item.total, 0);
    return { items, total };
  };

  const handleSaveQuote = async () => {
    setError("");
    setSuccess("");

    if (!projectName.trim()) {
      setError("Please enter a project name");
      return;
    }
    if (!hasProducts && sundryItems.length === 0) {
      setError("Please enter quantities for at least one product");
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      // Generate BOM when saving
      const { items: bomItems, total: totalPrice } = calculateBOM();
      
      // Build products list from quantities
      const products = PRODUCTS.filter(p => quantities[p.id] > 0).map(p => ({
        sku: p.sku,
        name: p.name,
        qty: quantities[p.id],
        linkedBase: p.linkedBase,
        requiredWings: p.requiredWings
      }));

      await axios.post(`${API}/half-height-turnstiles/configure`, {
        project_name: projectName,
        require_bases: requireBases === 'yes',
        products: products,
        sundry_items: sundryItems,
        bom_items: bomItems,
        total_price: totalPrice,
        pricing_tier: getUserPriceKey(),
        price_modifier: parseFloat(priceModifier) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Set generated BOM for display
      setGeneratedBOM(bomItems);
      setGeneratedTotal(totalPrice);
      setQuoteGenerated(true);
      setSuccess("Quote created and saved successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadQuote = async (installerOnly = false) => {
    const element = document.getElementById('quote-content');
    if (!element) return;

    try {
      if (installerOnly) {
        setInstallerOnlyCapture(true);
        await new Promise(r => setTimeout(r, 250));
      }
      const canvas = await html2canvas(element, { scale: 2 });
      const suffix = installerOnly ? 'Installer' : (isDistributor ? 'Distributor' : '');
      const link = document.createElement('a');
      link.download = `HalfHeight${suffix ? '-' + suffix : ''}-Quote-${projectName || 'Untitled'}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error('Error downloading quote:', err);
    } finally {
      if (installerOnly) setInstallerOnlyCapture(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Pedestrian Entrance Control
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <img src="/zkteco-logo.png" alt="ZKTeco" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800">Half Height Turnstiles</h1>
          <p className="text-slate-600">Configure tripod turnstiles and swing barriers</p>
        </div>

        {/* Messages */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <AlertDescription className="text-green-700">{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Configuration */}
          <div className="lg:col-span-1 space-y-6">
            {/* Project Details */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="project-name">Project Name *</Label>
                  <Input
                    id="project-name"
                    placeholder="e.g., Office Reception"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                </div>
                {/* Installer Discount - for distributors only */}
                {(isAdmin || isDistributor) && (
                  <div className="space-y-2">
                    <Label htmlFor="price-modifier">Installer Discount (%)</Label>
                    <Input
                      id="price-modifier"
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="e.g., 5 for 5% discount"
                      value={priceModifier}
                      onChange={(e) => setPriceModifier(e.target.value)}
                    />
                    <p className="text-xs text-slate-500">Enter discount percentage to reduce installer price</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Do you need bases?</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={requireBases === 'yes' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setRequireBases('yes');
                        setQuoteGenerated(false);
                        setGeneratedBOM([]);
                        setGeneratedTotal(0);
                      }}
                      className={requireBases === 'yes' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                    >
                      Yes
                    </Button>
                    <Button
                      type="button"
                      variant={requireBases === 'no' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setRequireBases('no');
                        setQuoteGenerated(false);
                        setGeneratedBOM([]);
                        setGeneratedTotal(0);
                      }}
                      className={requireBases === 'no' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                    >
                      No
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">Bases allow cable routing without floor modifications</p>
                </div>
                
                {/* Quote Target Selector - ZKTeco Staff Only */}
                <QuoteTargetSelector 
                  quoteTarget={quoteTarget} 
                  setQuoteTarget={setQuoteTarget} 
                  userRole={user?.role || ''} 
                />
              </CardContent>
            </Card>

            {/* Product Quantities */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Product Quantities
                </CardTitle>
                <CardDescription>Enter the quantity for each product</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {PRODUCTS.map(product => (
                  <div 
                    key={product.id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:border-teal-300 transition-colors"
                  >
                    <div className="w-14 h-14 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{product.name}</p>
                      <p className="text-xs text-slate-500">{product.description}</p>
                      {product.requiredWings && (
                        <p className="text-xs text-purple-600">+ Wings included</p>
                      )}
                      {product.datasheet && (
                        <a 
                          href={product.datasheet}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <FileText className="h-3 w-3" />
                          Datasheet
                        </a>
                      )}
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        min="0"
                        value={quantities[product.id] || ''}
                        onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                        placeholder="0"
                        className="text-center h-10 text-lg font-semibold"
                      />
                    </div>
                  </div>
                ))}

                {/* SBT3000 Note */}
                <div className="p-2 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-xs text-purple-700">
                    <Info className="h-3 w-3 inline mr-1" />
                    SBT3000 swing barriers require acrylic wings (automatically included).
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAll}
                    className="flex-1"
                  >
                    Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Quote & BOM */}
          <div className="lg:col-span-2">
            {/* Create Quote Button - Always visible when products selected */}
            {(hasProducts || sundryItems.length > 0) && !quoteGenerated && (
              <Card className="mb-6">
                <CardContent className="py-8">
                  <div className="text-center">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-teal-600" />
                    <h3 className="text-lg font-semibold mb-2">Ready to generate your quote?</h3>
                    <p className="text-slate-500 mb-6 text-sm">
                      You've selected {Object.values(quantities).reduce((a, b) => a + b, 0)} product(s).
                      Click below to create and save your Bill of Materials.
                    </p>
                    <Button
                      size="lg"
                      className="bg-teal-600 hover:bg-teal-700"
                      onClick={handleSaveQuote}
                      disabled={saving || !projectName.trim()}
                    >
                      <Save className="h-5 w-5 mr-2" />
                      {saving ? "Creating Quote..." : "Create Quote & Save"}
                    </Button>
                    {!projectName.trim() && (
                      <p className="text-sm text-amber-600 mt-3">
                        <Info className="h-4 w-4 inline mr-1" />
                        Please enter a project name first
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State - No products selected */}
            {!hasProducts && sundryItems.length === 0 && (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-slate-400">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Enter quantities to build your quote</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* BOM Display - Only shown after quote is generated */}
            {quoteGenerated && generatedBOM.length > 0 && (
              <Card id="quote-content">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-lg">Bill of Materials</CardTitle>
                      <CardDescription>
                        {projectName || 'Untitled Project'} 
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Quote For</p>
                      <p className="font-semibold text-teal-600 capitalize">{quoteTarget || getUserPriceKey()}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Base pricing (POA) call-to-action - only when quote includes bases */}
                  {generatedBOM.some(it => it.type === 'base') && (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 mb-4 print:hidden" data-testid="base-poa-banner">
                      <div className="flex items-start gap-2">
                        <Mail className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-blue-900">Your quote includes bases (price on application)</p>
                          <p className="text-xs text-blue-700">Base pricing isn't shown on-screen. Email ZKTeco to request a quote for the bases below.</p>
                        </div>
                      </div>
                      <Button
                        data-testid="email-base-pricing-button"
                        className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                        onClick={() => {
                          const bases = generatedBOM.filter(it => it.type === 'base');
                          const lines = bases.map(b => `- ${b.sku || ''} (${b.description || ''}) x ${b.qty || 1}`).join('\n');
                          const subject = `Base pricing request${projectName ? ' - ' + projectName : ''}`;
                          const body = `Hi ZKTeco,\n\nPlease provide base pricing for the following items on our quote${projectName ? ' (' + projectName + ')' : ''}:\n\n${lines}\n\nThank you.`;
                          window.location.href = `mailto:uk.office@zkteco.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                        }}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email ZKTeco for base pricing
                      </Button>
                    </div>
                  )}
                  {/* BOM Table */}
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead>SKU</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-center">Qty</TableHead>
                        {showBomDistributorPrice && (
                          <>
                            <TableHead className="text-right text-purple-700">Dist. Price</TableHead>
                            <TableHead className="text-right text-purple-700">Dist. Total</TableHead>
                          </>
                        )}
                        {showBomInstallerPrice && (
                          <>
                            <TableHead className="text-right text-green-700">Inst. Price</TableHead>
                            <TableHead className="text-right text-green-700">Inst. Total</TableHead>
                          </>
                        )}
                        {showBomEndUserPrice && (
                          <>
                            <TableHead className="text-right text-orange-700">EU Price</TableHead>
                            <TableHead className="text-right text-orange-700">EU Total</TableHead>
                          </>
                        )}
                        {!showBomDistributorPrice && !showBomInstallerPrice && !showBomEndUserPrice && (
                          <>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {generatedBOM.map((item, index) => (
                        <TableRow 
                          key={index}
                          className={
                            item.type === 'wings' ? 'bg-purple-50' :
                            item.type === 'base' ? 'bg-green-50' :
                            item.type === 'delivery' ? 'bg-blue-50' :
                            item.type === 'sundry' ? 'bg-yellow-50' : ''
                          }
                        >
                          <TableCell className="font-mono text-xs">{item.sku}</TableCell>
                          <TableCell>
                            {item.description}
                            {item.type === 'wings' && <span className="text-purple-600 text-xs ml-1">(required)</span>}
                            {item.type === 'base' && <span className="text-green-600 text-xs ml-1">(base)</span>}
                            {item.type === 'delivery' && <span className="text-blue-600 text-xs ml-1">(delivery)</span>}
                            {item.type === 'base' && (
                              <a href={`mailto:uk.office@zkteco.co.uk?subject=${encodeURIComponent('Base pricing request - ' + (item.sku || ''))}&body=${encodeURIComponent('Please provide base pricing for ' + (item.sku || '') + ' (' + (item.description || '') + '), qty ' + (item.qty || 1) + '.')}`}
                                className="block text-xs text-blue-600 underline mt-0.5" data-testid="base-poa-email">Email ZKTeco for base pricing</a>
                            )}
                          </TableCell>
                          <TableCell className="text-center">{item.qty}</TableCell>
                          {showBomDistributorPrice && (
                            <>
                              <TableCell className="text-right text-purple-700">{item.type === 'base' ? 'POA' : `£${item.distributorPrice?.toFixed(2) || item.unitPrice.toFixed(2)}`}</TableCell>
                              <TableCell className="text-right font-medium text-purple-700">{item.type === 'base' ? 'POA' : `£${item.distributorTotal?.toFixed(2) || item.total.toFixed(2)}`}</TableCell>
                            </>
                          )}
                          {showBomInstallerPrice && (
                            <>
                              <TableCell className="text-right text-green-700">{item.type === 'base' ? 'POA' : `£${item.installerPrice?.toFixed(2) || item.unitPrice.toFixed(2)}`}</TableCell>
                              <TableCell className="text-right font-medium text-green-700">{item.type === 'base' ? 'POA' : `£${item.installerTotal?.toFixed(2) || item.total.toFixed(2)}`}</TableCell>
                            </>
                          )}
                          {showBomEndUserPrice && (
                            <>
                              <TableCell className="text-right text-orange-700">{item.type === 'base' ? 'POA' : `£${item.endUserPrice?.toFixed(2) || item.unitPrice.toFixed(2)}`}</TableCell>
                              <TableCell className="text-right font-medium text-orange-700">{item.type === 'base' ? 'POA' : `£${item.endUserTotal?.toFixed(2) || item.total.toFixed(2)}`}</TableCell>
                            </>
                          )}
                          {!showBomDistributorPrice && !showBomInstallerPrice && !showBomEndUserPrice && (
                            <>
                              <TableCell className="text-right">{item.type === 'base' ? 'POA' : `£${item.unitPrice.toFixed(2)}`}</TableCell>
                              <TableCell className="text-right font-medium">{item.type === 'base' ? 'POA' : `£${item.total.toFixed(2)}`}</TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Total */}
                  <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                    {showBomDistributorPrice && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-lg font-semibold text-purple-700">Distributor Total</span>
                        <span className="text-2xl font-bold text-purple-600">
                          £{generatedBOM.filter(i => i.type !== 'base').reduce((sum, item) => sum + (item.distributorTotal || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {showBomInstallerPrice && (
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-green-700">Installer Total</span>
                        <span className="text-2xl font-bold text-green-600">
                          £{generatedBOM.filter(i => i.type !== 'base').reduce((sum, item) => sum + (item.installerTotal || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {showBomEndUserPrice && (
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-orange-700">End User Total</span>
                        <span className="text-2xl font-bold text-orange-600">
                          £{generatedBOM.filter(i => i.type !== 'base').reduce((sum, item) => sum + (item.endUserTotal || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {!showBomDistributorPrice && !showBomInstallerPrice && !showBomEndUserPrice && (
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-teal-800">Total</span>
                        <span className="text-2xl font-bold text-teal-600">£{generatedBOM.filter(i => i.type !== 'base').reduce((sum, item) => sum + (item.total || 0), 0).toFixed(2)}</span>
                      </div>
                    )}
                    {showBomDistributorPrice && showBomInstallerPrice && (
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-teal-200">
                        <span className="text-sm font-medium text-green-700">Your Margin</span>
                        <span className="text-lg font-bold text-green-600">
                          £{(generatedBOM.filter(i => i.type !== 'base').reduce((sum, item) => sum + (item.installerTotal || 0), 0) - 
                             generatedBOM.filter(i => i.type !== 'base').reduce((sum, item) => sum + (item.distributorTotal || 0), 0)).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Saved confirmation */}
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-green-700 text-sm font-medium">
                      ✓ Quote saved successfully
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons - Only after quote generated */}
            {quoteGenerated && generatedBOM.length > 0 && (
              <div className="flex justify-end gap-3 mt-4">
                {isDistributor ? (
                  <>
                    <Button
                      variant="outline"
                      data-testid="download-distributor-quote-btn"
                      onClick={() => handleDownloadQuote(false)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Distributor Quote
                    </Button>
                    <Button
                      variant="outline"
                      data-testid="download-installer-quote-btn"
                      onClick={() => handleDownloadQuote(true)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Installer Quote
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadQuote(false)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Quote
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setQuoteGenerated(false);
                    setGeneratedBOM([]);
                    setGeneratedTotal(0);
                  }}
                >
                  Edit Configuration
                </Button>
              </div>
            )}

            {/* Add Sundry Item - Only when not yet generated */}
            {(hasProducts || sundryItems.length > 0) && !quoteGenerated && (
              <Card className="mt-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Sundry Items</CardTitle>
                  <CardDescription>Add custom line items before generating quote</CardDescription>
                </CardHeader>
                <CardContent>
                  {sundryItems.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {sundryItems.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-2 bg-yellow-50 rounded border border-yellow-200">
                          <span className="text-sm">{item.description}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">£{item.price.toFixed(2)}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                              onClick={() => handleRemoveSundryItem(item.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {showSundryDialog ? (
                    <div className="p-4 border rounded-lg bg-yellow-50">
                      <Label className="text-sm font-medium mb-2 block">Add Sundry Item</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Description"
                          value={sundryDescription}
                          onChange={(e) => setSundryDescription(e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          placeholder="Price"
                          value={sundryPrice}
                          onChange={(e) => setSundryPrice(e.target.value)}
                          className="w-24"
                        />
                        <Button onClick={handleAddSundryItem} className="bg-yellow-600 hover:bg-yellow-700">
                          Add
                        </Button>
                        <Button variant="outline" onClick={() => setShowSundryDialog(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setShowSundryDialog(true)}
                      className="w-full border-dashed"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Sundry Item
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HalfHeightTurnstiles;
